/**
 * Iter XX.70 — VideoUpgradeSheet
 *
 * Inline slide-up sheet that appears when the player taps a locked
 * insight or the "Continue with Gully Plus" button. Does NOT
 * navigate away from the chat (Constitution §4). On confirmation,
 * calls /api/video-review/{id}/checkout to mint a Stripe session
 * and redirects. On return the chat re-hydrates and the reveal
 * animation runs automatically.
 *
 * When `founderPreview` is true, the Upgrade button is replaced with
 * a "Simulate successful payment" action that fires ONLY the
 * client-side reveal — never touches billing.
 *
 * Data-testids:
 *   video-upgrade-sheet
 *   video-upgrade-sheet-dismiss
 *   video-upgrade-sheet-cta
 *   video-upgrade-sheet-preview-cta  (only in founder preview)
 */
import React, { useState } from "react";
import api from "../lib/api";

export default function VideoUpgradeSheet({
  videoAnalysisId, founderPreview, onDismiss,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleUpgrade = async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await api.post(
        `/video-review/${encodeURIComponent(videoAnalysisId)}/checkout`,
      );
      if (data?.url) {
        window.location.href = data.url;
      } else {
        setError("Couldn't reach Stripe. Try again in a moment.");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "checkout_failed";
      if (detail === "stripe_video_upgrade_disabled") {
        setError("Upgrade isn't enabled for this account yet.");
      } else {
        setError("Couldn't start checkout. Try again in a moment.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFounderSimulate = () => {
    // Founder preview: never touch billing. Fire a client-side event
    // and dispatch a global signal so VideoReviewCard can re-render
    // with the "paid" shape (parent decides how to simulate).
    try {
      window.dispatchEvent(new CustomEvent("gully:founder_preview_simulate_payment", {
        detail: { video_analysis_id: videoAnalysisId },
      }));
    } catch { /* swallow */ }
    onDismiss?.();
  };

  return (
    <div
      data-testid="video-upgrade-sheet"
      role="dialog"
      aria-labelledby="video-upgrade-sheet-title"
      className="fixed inset-x-0 bottom-0 z-40 sm:inset-0 sm:flex sm:items-end sm:justify-center sm:p-4"
    >
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onDismiss}
        aria-hidden
      />
      <div className="relative w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl border border-slate-800 bg-slate-950 p-4 sm:p-5 shadow-2xl">
        <button
          type="button"
          data-testid="video-upgrade-sheet-dismiss"
          onClick={onDismiss}
          className="absolute right-3 top-3 rounded-md text-slate-500 hover:text-slate-300 text-lg leading-none w-7 h-7"
          aria-label="Dismiss"
        >
          ×
        </button>
        <div
          id="video-upgrade-sheet-title"
          className="text-sm font-medium text-slate-100 pr-8"
        >
          Continue with Gully Plus
        </div>
        <div className="mt-1 text-[13px] text-slate-400 leading-snug">
          You’ve read the first insight. Gully Plus opens the rest of
          this review, keeps every video in one connected picture of
          your cricket, and continues with you across the season.
        </div>
        <ul className="mt-3 space-y-1.5 text-[12px] text-slate-400">
          <li>· See every insight from this review</li>
          <li>· Every new video builds on your last one</li>
          <li>· Your full timeline &amp; season book</li>
          <li>· Cancel anytime — everything captured stays readable</li>
        </ul>
        {error && (
          <div className="mt-3 text-xs text-red-400">{error}</div>
        )}
        {founderPreview ? (
          <button
            type="button"
            data-testid="video-upgrade-sheet-preview-cta"
            onClick={handleFounderSimulate}
            className="mt-4 w-full rounded-xl bg-amber-600 py-2.5 text-sm font-medium text-white hover:bg-amber-500 transition-colors"
          >
            Simulate successful payment (preview only)
          </button>
        ) : (
          <button
            type="button"
            data-testid="video-upgrade-sheet-cta"
            onClick={handleUpgrade}
            disabled={loading}
            className="mt-4 w-full rounded-xl bg-emerald-600 py-2.5 text-sm font-medium text-white hover:bg-emerald-500 transition-colors disabled:opacity-60"
          >
            {loading ? "Opening Stripe…" : "Continue with Gully Plus"}
          </button>
        )}
      </div>
    </div>
  );
}
